import React, { useEffect, useState } from 'react';
import { LeftOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, Row, Switch, Tag, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';

import BasicButtonPrimary from 'components/BasicButtonPrimary';
import ThirdPartyIntegration from 'services/thirdPartyIntegrationService';
import './TingeeThirdParty.scss';
import { THIRDPARTY_CODE } from 'constants/app';
import { getThirdPartyIdFromStationIntegrationList } from './helpers/thirdPartyIntegrationHelper';

const KimNganBusinessThirdParty = ({ id, setOpenContent, setId }) => {
  const { t: translation } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (id) {
      handleGetData(id);
    }
  }, [id]);

  const handleSetupData = (res = {}) => {
    const partyRequiredData = JSON.parse(res?.partyRequiredData || '{}');
    const connected = res?.partyActiveStatus === 1;

    form.setFieldsValue({
      partyUrl: partyRequiredData?.partyUrl,
      token_key: partyRequiredData?.token_key,
      secret_key: partyRequiredData?.secret_key,
      isConnected: connected,
      isEnable: res?.partyEnableStatus === 1,
    });

    setIsConnected(connected);
  };

  const handleGetData = async (targetId = id) => {
    if (!targetId) return;
    try {
      setLoading(true);
      const res = await ThirdPartyIntegration.getThirdPartyById({ id: targetId });
      handleSetupData(res);
    } catch (error) {
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleTestKimNgan = async (showError = true, valuesArg) => {
    try {
      setLoading(true);
      const values = valuesArg || form.getFieldsValue(true);

      const payload = {
        partyUrl: values.partyUrl,
        token_key: values.token_key,
        secret_key: values.secret_key,
        amount: 10000,
      };

      const result = await ThirdPartyIntegration.testConfigsKimNganBusiness(payload);

      if (result) {
        setIsConnected(true);
        form.setFieldsValue({ isConnected: true });

        if (result?.paymentLinkUrl) {
          window.open(result.paymentLinkUrl, '_blank');
        }

        notification['success']({
          message: '',
          description: translation('vietqr.successMessage'),
        });
        return true;
      }

      return false;
    } catch (error) {
      if (showError) {
        notification['error']({
          message: '',
          description: error?.message || translation('vietqr.errorMessage'),
        });
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (values) => {
    try {
      setLoading(true);
      let isConnectedLocal = isConnected;

      if (!isConnectedLocal) {
        const testSuccess = await handleTestKimNgan(true, values);
        if (!testSuccess) return;
        isConnectedLocal = true;
        values = { ...values, isEnable: true };
        form.setFieldsValue({ isEnable: true, isConnected: true });
      }

      const payload = {
        data: {
          partyRequiredData: {
            partyUrl: values.partyUrl,
            token_key: values.token_key,
            secret_key: values.secret_key,
          },
          partyActiveStatus: isConnectedLocal ? 1 : 0,
          partyEnableStatus: values.isEnable ? 1 : 0,
        },
      };

      await ThirdPartyIntegration.updateConfigsKimNganBusiness(payload);
      const updatedThirdPartyId = await getThirdPartyIdFromStationIntegrationList(THIRDPARTY_CODE.KIMNGAN_BUSINESS);
      if (!updatedThirdPartyId) {
        throw new Error('KIMNGAN_ID_NOT_FOUND_FROM_LIST');
      }
      if (Number(updatedThirdPartyId) !== Number(id)) {
        setId(updatedThirdPartyId);
      } else {
        await handleGetData(updatedThirdPartyId);
      }

      notification['success']({
        message: '',
        description: translation('tingee.updateSuccess'),
      });
    } catch (error) {
      notification['error']({
        message: '',
        description: translation('tingee.updateError'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='tingee-noti'>
      <Button
        type='default'
        icon={<LeftOutlined />}
        onClick={() => {
          setOpenContent('');
          setId('');
        }}
      />
      <Card loading={loading} className='telegram-noti_content'>
        <div className='mt-2'>
          <Form form={form} layout='vertical' onFinish={handleUpdateConfig}>
            <Row gutter={[48, 16]}>
              <Col xs={{ span: 24 }} md={{ span: 20 }} span={20}>
                <div className='section-title mb-2 mt-3'>
                  {translation('expand_setting.KIMNGAN_BUSINESS')}
                </div>

                <Form.Item
                  required
                  label='Base URL'
                  name='partyUrl'
                  rules={[{ required: true, message: translation('isReq') }]}
                >
                  <Input type='text' />
                </Form.Item>

                <Form.Item
                  required
                  label='Token Key'
                  name='token_key'
                  rules={[{ required: true, message: translation('isReq') }]}
                >
                  <Input type='text' />
                </Form.Item>

                <Form.Item
                  required
                  label='Secret Key'
                  name='secret_key'
                  rules={[{ required: true, message: translation('isReq') }]}
                >
                  <Input type='text' />
                </Form.Item>

                <Form.Item
                  label={translation('telegram.active')}
                  name='isEnable'
                  valuePropName='checked'
                >
                  <Switch />
                </Form.Item>

                <Form.Item
                  className='pt-1'
                  label={translation('telegram.connectionStatus')}
                >
                  <Tag color={form.getFieldValue('isConnected') ? 'green' : ''}>
                    {form.getFieldValue('isConnected')
                      ? translation('telegram.connected')
                      : translation('telegram.notConnected')}
                  </Tag>
                </Form.Item>

                <BasicButtonPrimary
                  style={{ marginRight: 10 }}
                  className='mr-2'
                  htmlType='submit'
                  text={translation('telegram.updateButton')}
                />

                <Button loading={loading} onClick={() => handleTestKimNgan(true)} className='ml-2'>
                  Test cấu hình
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Card>
    </div>
  );
};

export default KimNganBusinessThirdParty;
